"use client";

import { useState, useEffect } from "react";
import { X, Settings, Shield, ChartLine, Megaphone } from "lucide-react";
import { CONSENT_VERSION, readStoredConsent, storeConsent } from "@/lib/consent";

/**
 * DSGVO/TDDDG-konformer Cookie-Banner mit drei Stufen:
 *
 *  - notwendig:    immer an, technisch erforderlich (Login-Session,
 *                  Cookie-Consent-Speicher, Wartungsmodus-Bypass)
 *  - statistik:    Vercel Analytics + Speed Insights – aktuell COOKIELESS,
 *                  bräuchte streng genommen kein Consent. Toggle dient
 *                  Transparenz und einer möglichen späteren Erweiterung.
 *  - marketing:    Aktuell KEINE Marketing-Cookies aktiv. Toggle bleibt
 *                  als Stub für künftige Tracking-Pixel (Meta, Google Ads).
 *
 * Wichtige Eigenschaften nach EuGH-Rechtsprechung:
 *   • „Alle akzeptieren" und „Nur notwendige" sind GLEICHWERTIG sichtbar
 *     (kein Dark-Pattern wie unauffälliger Reject-Link).
 *   • X-Schließen entspricht „Nur notwendige" – keine implizite Zustimmung.
 *   • Einstellungen jederzeit über Sidebar-Footer-Link „Cookies"
 *     erneut zugänglich (saved consent kann widerrufen werden).
 */

type Choice = boolean | undefined;
type ConsentSettings = {
  necessary: true;
  statistics: Choice;
  marketing: Choice;
};

/**
 * `initialOpen` kommt vom Server (Root-Layout liest das Consent-Cookie):
 * Ohne gültige Einwilligung steht der Banner bereits im HTML – kein
 * Nachladen nach der Hydration, keine Layout-Verschiebung.
 */
export function CookieBanner({ initialOpen }: { initialOpen: boolean }) {
  const [visible, setVisible] = useState(initialOpen);
  const [showSettings, setShowSettings] = useState(false);
  const [statistics, setStatistics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Migration: Einwilligung bisher nur in localStorage → ins Cookie
  // übernehmen, damit der Server sie beim nächsten Aufruf kennt.
  useEffect(() => {
    if (!initialOpen) return;
    const stored = readStoredConsent();
    if (stored) {
      storeConsent(stored);
      setVisible(false);
    }
  }, [initialOpen]);

  // Erlaubt Re-Open via Custom-Event (z.B. „Cookies"-Link in Sidebar)
  useEffect(() => {
    const handler = () => {
      // Vorhandene Werte vorbelegen
      const stored = readStoredConsent();
      if (stored) {
        setStatistics(stored.statistics);
        setMarketing(stored.marketing);
      }
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener("cookie-banner:reopen", handler);
    return () => window.removeEventListener("cookie-banner:reopen", handler);
  }, []);

  const persist = (consent: ConsentSettings) => {
    storeConsent({
      v: CONSENT_VERSION,
      necessary: true,
      statistics: !!consent.statistics,
      marketing: !!consent.marketing,
      ts: Date.now(),
    });
    setVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () =>
    persist({ necessary: true, statistics: true, marketing: true });
  const acceptNecessaryOnly = () =>
    persist({ necessary: true, statistics: false, marketing: false });
  const saveCustom = () =>
    persist({ necessary: true, statistics, marketing });

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-4 sm:items-center">
      <div
        className="w-full max-w-xl rounded-2xl border p-6 shadow-2xl"
        style={{
          backgroundColor: "var(--bg)",
          borderColor: "var(--border)",
        }}
        role="dialog"
        aria-label="Cookie-Einstellungen"
        aria-modal="true"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-brand-500" />
            <h2 className="text-lg font-bold" style={{ color: "var(--text)" }}>
              Datenschutz & Cookies
            </h2>
          </div>
          <button
            onClick={acceptNecessaryOnly}
            className="rounded-full p-1 transition-colors hover:bg-brand-500/10"
            aria-label="Schließen – nur notwendige akzeptieren"
          >
            <X className="h-5 w-5" style={{ color: "var(--text-secondary)" }} />
          </button>
        </div>

        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          Wir verwenden technisch notwendige Speicher (z. B. für die
          Anmeldung). Statistik (Google Analytics) nur mit Ihrer Zustimmung –
          sie hilft uns, die Website zu verbessern.
        </p>

        {showSettings && (
          <div className="mt-5 space-y-3">
            <Row
              icon={Shield}
              title="Notwendig"
              desc="Login-Session, Cookie-Einstellung, Wartungsmodus. Ohne diese funktioniert die Seite nicht."
              checked
              disabled
              onChange={() => {}}
            />
            <Row
              icon={ChartLine}
              title="Statistik (Google Analytics)"
              desc="Google Analytics lädt erst nach Ihrer Zustimmung (Consent Mode, IP-Anonymisierung). Cookielose Seitenaufruf-Zahlen (Vercel Web Analytics) laufen unabhängig davon."
              checked={statistics}
              onChange={setStatistics}
            />
            <Row
              icon={Megaphone}
              title="Marketing"
              desc="Aktuell sind keine Marketing-Cookies aktiv. Diese Option ist für eine spätere Schaltung von Werbe-Pixeln (z. B. Meta, Google Ads) vorgesehen."
              checked={marketing}
              onChange={setMarketing}
            />
          </div>
        )}

        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          {/* „Alle akzeptieren“ und „Nur notwendige“ gleich gestaltet (DSK-Orientierungshilfe). */}
          <button
            onClick={acceptAll}
            className="flex-1 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
          >
            Alle akzeptieren
          </button>
          <button
            onClick={acceptNecessaryOnly}
            className="flex-1 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
          >
            Nur notwendige
          </button>
          {showSettings ? (
            <button
              onClick={saveCustom}
              className="flex-1 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-brand-500/10"
              style={{ color: "var(--text)", borderColor: "var(--border)" }}
            >
              Auswahl speichern
            </button>
          ) : (
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center justify-center gap-1 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-brand-500/10"
              style={{ color: "var(--text)", borderColor: "var(--border)" }}
            >
              <Settings className="h-4 w-4" />
              Einstellungen
            </button>
          )}
        </div>

        <p
          className="mt-3 text-center text-xs"
          style={{ color: "var(--text-tertiary)" }}
        >
          Mehr in unserer{" "}
          <a href="/datenschutz" className="underline hover:text-brand-500">
            Datenschutzerklärung
          </a>{" "}
          – jederzeit unter &bdquo;Cookies&ldquo; im Footer änderbar.
        </p>
      </div>
    </div>
  );
}

function Row({
  icon: Icon,
  title,
  desc,
  checked,
  disabled,
  onChange,
}: {
  icon: typeof Shield;
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      className="flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3"
      style={{ borderColor: "var(--border)" }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded accent-brand-500"
      />
      <div className="flex-1">
        <div className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" style={{ color: "var(--text-tertiary)" }} />
          <span className="text-sm font-semibold" style={{ color: "var(--text)" }}>
            {title}
          </span>
          {disabled && (
            <span
              className="rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
              style={{
                backgroundColor: "var(--bg-secondary)",
                color: "var(--text-tertiary)",
              }}
            >
              immer aktiv
            </span>
          )}
        </div>
        <p
          className="mt-0.5 text-xs leading-relaxed"
          style={{ color: "var(--text-tertiary)" }}
        >
          {desc}
        </p>
      </div>
    </label>
  );
}
